import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  PatchAdminV1StoresStoreIdShippingRatesIdBody,
  PatchAdminV1StoresStoreIdShippingZonesIdBody,
  PostAdminV1StoresStoreIdShippingZonesBody,
  PostAdminV1StoresStoreIdShippingZonesIdRatesBody,
} from '@nucleus-commerce/api-client/admin';

import { shippingApi } from '../api/shipping-api';

import { shippingKeys } from './shipping-keys';

/** Rates are embedded in their zone, so every change refetches the one zones list. */
function useInvalidateZones(storeId: string) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: shippingKeys.all(storeId) });
}

export function useCreateZone(storeId: string) {
  const invalidate = useInvalidateZones(storeId);
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdShippingZonesBody) =>
      shippingApi.createZone(storeId, body),
    meta: { successMessage: 'shipping:toasts.zoneCreated', silentError: true },
    onSuccess: invalidate,
  });
}

export function useUpdateZone(storeId: string) {
  const invalidate = useInvalidateZones(storeId);
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PatchAdminV1StoresStoreIdShippingZonesIdBody;
    }) => shippingApi.updateZone(storeId, id, body),
    meta: { successMessage: 'shipping:toasts.zoneUpdated', silentError: true },
    onSuccess: invalidate,
  });
}

export function useDeleteZone(storeId: string) {
  const invalidate = useInvalidateZones(storeId);
  return useMutation({
    mutationFn: (id: string) => shippingApi.removeZone(storeId, id),
    meta: { successMessage: 'shipping:toasts.zoneDeleted', silentError: true },
    onSuccess: invalidate,
  });
}

export function useCreateRate(storeId: string) {
  const invalidate = useInvalidateZones(storeId);
  return useMutation({
    mutationFn: ({
      zoneId,
      body,
    }: {
      zoneId: string;
      body: PostAdminV1StoresStoreIdShippingZonesIdRatesBody;
    }) => shippingApi.createRate(storeId, zoneId, body),
    meta: { successMessage: 'shipping:toasts.rateCreated', silentError: true },
    onSuccess: invalidate,
  });
}

export function useUpdateRate(storeId: string) {
  const invalidate = useInvalidateZones(storeId);
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PatchAdminV1StoresStoreIdShippingRatesIdBody;
    }) => shippingApi.updateRate(storeId, id, body),
    meta: { successMessage: 'shipping:toasts.rateUpdated', silentError: true },
    onSuccess: invalidate,
  });
}

export function useDeleteRate(storeId: string) {
  const invalidate = useInvalidateZones(storeId);
  return useMutation({
    mutationFn: (id: string) => shippingApi.removeRate(storeId, id),
    meta: { successMessage: 'shipping:toasts.rateDeleted', silentError: true },
    onSuccess: invalidate,
  });
}
